/**
 * TokenBalance - Widget affichant le solde de crédits
 * Affiche le solde dans le header avec alerte si bas
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTokens } from '../context/TokenContext';
import { Coins, AlertTriangle, Plus, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { cn } from '../lib/utils';

export const TokenBalance = ({ compact = false }) => {
  const navigate = useNavigate();
  const { balance, loading, tier } = useTokens();

  // Seuils d'alerte
  const isLow = balance < 100;
  const isCritical = balance < 25;

  if (loading) {
    return (
      <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-[#1E293B]/50">
        <Loader2 className="w-4 h-4 text-[#94A3B8] animate-spin" />
        {!compact && <span className="text-xs text-[#94A3B8]">...</span>}
      </div>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/pricing')}
            className={cn(
              "flex items-center gap-1.5 px-2 lg:px-3 py-1.5 rounded-lg transition-all",
              "hover:bg-white/5",
              isCritical && "bg-red-500/10 hover:bg-red-500/20",
              isLow && !isCritical && "bg-amber-500/10 hover:bg-amber-500/20"
            )}
          >
            {/* Icône */}
            {isCritical ? (
              <AlertTriangle className="w-4 h-4 text-red-400" />
            ) : isLow ? (
              <AlertTriangle className="w-4 h-4 text-amber-400" />
            ) : (
              <Coins className="w-4 h-4 text-[#B69D74]" />
            )}
            
            {/* Solde */}
            <span className={cn(
              "text-sm font-medium tabular-nums",
              isCritical ? "text-red-400" : 
              isLow ? "text-amber-400" : 
              "text-[#B69D74]"
            )}>
              {balance.toLocaleString('fr-FR')}
            </span>
            
            {/* Label (desktop only) */}
            {!compact && (
              <span className="hidden lg:inline text-xs text-[#94A3B8]">
                crédits
              </span>
            )}
            
            {/* Bouton + (si solde bas) */}
            {isLow && (
              <Plus className="w-3.5 h-3.5 text-[#94A3B8] ml-0.5" />
            )}
          </Button>
        </TooltipTrigger>
        
        <TooltipContent 
          side="bottom" 
          className="bg-[#1E293B] border-[#B69D74]/20 text-[#F8FAFC]"
        >
          <div className="text-center py-1">
            <div className="font-medium">{balance.toLocaleString('fr-FR')} crédits</div>
            <div className="text-xs text-[#94A3B8] mt-0.5">
              {tier === 'free' && 'Plan Gratuit'}
              {tier === 'starter' && 'Plan Starter'}
              {tier === 'professionnel' && 'Plan Professionnel'}
              {tier === 'cabinet' && 'Plan Cabinet'}
            </div>
            {isLow && (
              <div className="text-xs text-amber-400 mt-1">
                Cliquez pour recharger
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * TokenBalanceBadge - Version compacte pour mobile
 */
export const TokenBalanceBadge = () => {
  const { balance, loading } = useTokens();
  const isLow = balance < 100;
  
  if (loading) return null;

  return (
    <div className={cn(
      "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium",
      isLow ? "bg-amber-500/20 text-amber-400" : "bg-[#B69D74]/20 text-[#B69D74]"
    )}>
      <Coins className="w-3 h-3" />
      {balance.toLocaleString('fr-FR')}
    </div>
  );
};

export default TokenBalance;
